/**
 * Dataset Details Page
 *
 * Displays comprehensive information about a dataset including:
 * - Metadata (name, description, source, etc.)
 * - Schema/columns with linked schema library info
 * - Associated parquet files
 * - Preview of data
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, FileText, Database, Table, AlertCircle, Link2, Unlink, Loader2, Pencil, Check, X, Trash2, Search, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDataset, useDatasetSchema, useDatasetPreview, useUpdateDataset } from '@/hooks/useDatasets';
import { DeleteDatasetDialog } from '@/components/datasets/DeleteDatasetDialog';
import { useLinkDatasetToSchema, useUnlinkDatasetFromSchema } from '@/features/schemas/hooks/useSchemas';
import { SchemaPickerDialog } from '@/features/schemas/components/SchemaPickerDialog';
import { CompatibilityBadge } from '@/features/schemas/components/CompatibilityBadge';
import { DatasetExplore } from '@/features/datasets/components/DatasetExplore';
import { ClassificationSection } from '@/features/classification/components/ClassificationSection';
import { useToast } from '@/hooks/useToast';
import { formatBytes } from '@/lib/utils';
import type { Dataset, DatasetField, DatasetPreview } from '@/types';
import type { LinkedSchemaInfo, CompatibilityMode, SchemaListItem } from '@/features/schemas/types/schema.types';

/** Regex for valid dataset name: starts with letter, alphanumeric/underscore/hyphen only */
const DATASET_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_-]*$/;

/**
 * Inline Editable Name Component
 *
 * Renders the dataset name as a heading. Click the pencil icon (or the name
 * itself) to enter edit mode. Press Enter or click the check to save, Escape
 * or click X to cancel. Shows validation inline.
 */
const InlineEditableName: React.FC<{
  datasetId: string;
  name: string;
}> = ({ datasetId, name }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const [validationError, setValidationError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const updateDataset = useUpdateDataset();

  // Sync local value when the prop changes (e.g. after refetch). Adjusted
  // during render (not an effect): mirrors both `name` and `editing` — both
  // stable primitives — since the reset must fire on either changing alone
  // (e.g. toggling `editing: true → false` with `name` unchanged), matching
  // the two-mirror precedent used for EditableConnectorName's identical
  // shape. Converges after exactly one extra render.
  const [prevName, setPrevName] = useState(name);
  const [prevEditing, setPrevEditing] = useState(editing);
  if (name !== prevName || editing !== prevEditing) {
    setPrevName(name);
    setPrevEditing(editing);
    if (!editing) setValue(name);
  }

  // Auto-focus input when entering edit mode
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const validate = useCallback((v: string): string | null => {
    const trimmed = v.trim();
    if (!trimmed) return 'Name is required';
    if (trimmed.length > 255) return 'Name must be 255 characters or fewer';
    if (!DATASET_NAME_PATTERN.test(trimmed))
      return 'Name must start with a letter and contain only letters, numbers, underscores, or hyphens';
    return null;
  }, []);

  const save = useCallback(async () => {
    const trimmed = value.trim();
    const err = validate(trimmed);
    if (err) {
      setValidationError(err);
      return;
    }
    if (trimmed === name) {
      // No change -- just exit edit mode
      setEditing(false);
      setValidationError(null);
      return;
    }
    try {
      await updateDataset.mutateAsync({ id: datasetId, data: { name: trimmed } });
      toast({ title: 'Dataset name updated' });
      setEditing(false);
      setValidationError(null);
    } catch (error) {
      toast({
        title: 'Failed to update name',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [value, name, datasetId, updateDataset, toast, validate]);

  const cancel = useCallback(() => {
    setValue(name);
    setEditing(false);
    setValidationError(null);
  }, [name]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        save();
      } else if (e.key === 'Escape') {
        cancel();
      }
    },
    [save, cancel]
  );

  if (editing) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setValidationError(null);
            }}
            onKeyDown={handleKeyDown}
            maxLength={255}
            className="text-2xl font-bold h-auto py-1 px-2"
            disabled={updateDataset.isPending}
          />
          <Button
            size="icon"
            variant="ghost"
            onClick={save}
            disabled={updateDataset.isPending}
            aria-label="Save name"
          >
            {updateDataset.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4 text-success" />
            )}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={cancel}
            disabled={updateDataset.isPending}
            aria-label="Cancel editing name"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
        {validationError && (
          <p className="text-sm text-destructive">{validationError}</p>
        )}
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-2">
      <h1 className="text-3xl font-bold">{name}</h1>
      <button
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-accent"
        aria-label="Edit dataset name"
      >
        <Pencil className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  );
};

/**
 * Inline Editable Description Component
 *
 * Renders the dataset description as a paragraph. Click the pencil icon (or
 * the text) to enter edit mode with a textarea. Press Escape or click X to
 * cancel. Click the check button to save (Enter inserts a newline in the
 * textarea, so we don't use it for save).
 */
const InlineEditableDescription: React.FC<{
  datasetId: string;
  description: string | undefined;
}> = ({ datasetId, description }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(description ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();
  const updateDataset = useUpdateDataset();

  // Adjusted during render (not an effect) — same two-mirror shape as
  // InlineEditableName above: `description` and `editing` are both stable
  // primitives (string | undefined, boolean), and the reset must fire on
  // either changing alone. Converges after exactly one extra render.
  const [prevDescription, setPrevDescription] = useState(description);
  const [prevEditing, setPrevEditing] = useState(editing);
  if (description !== prevDescription || editing !== prevEditing) {
    setPrevDescription(description);
    setPrevEditing(editing);
    if (!editing) setValue(description ?? '');
  }

  useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  const save = useCallback(async () => {
    const trimmed = value.trim();
    if (trimmed.length > 1000) {
      setValidationError('Description must be 1000 characters or fewer');
      return;
    }
    if (trimmed === (description ?? '')) {
      setEditing(false);
      setValidationError(null);
      return;
    }
    try {
      await updateDataset.mutateAsync({ id: datasetId, data: { description: trimmed } });
      toast({ title: 'Dataset description updated' });
      setEditing(false);
      setValidationError(null);
    } catch (error) {
      toast({
        title: 'Failed to update description',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [value, description, datasetId, updateDataset, toast]);

  const cancel = useCallback(() => {
    setValue(description ?? '');
    setEditing(false);
    setValidationError(null);
  }, [description]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancel();
      }
    },
    [cancel]
  );

  if (editing) {
    return (
      <div className="space-y-1">
        <div className="flex items-start gap-2">
          <Textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setValidationError(null);
            }}
            onKeyDown={handleKeyDown}
            maxLength={1000}
            rows={3}
            className="text-sm resize-y"
            placeholder="Add a description..."
            disabled={updateDataset.isPending}
          />
          <div className="flex flex-col gap-1 pt-1">
            <Button
              size="icon"
              variant="ghost"
              onClick={save}
              disabled={updateDataset.isPending}
              aria-label="Save description"
            >
              {updateDataset.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4 text-success" />
              )}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              onClick={cancel}
              disabled={updateDataset.isPending}
              aria-label="Cancel editing description"
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </div>
        {validationError && (
          <p className="text-sm text-destructive">{validationError}</p>
        )}
        <p className="text-xs text-muted-foreground">{value.length}/1000</p>
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-2">
      <p className="text-muted-foreground">
        {description || 'No description available'}
      </p>
      <button
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-accent"
        aria-label="Edit dataset description"
      >
        <Pencil className="h-4 w-4 text-muted-foreground" />
      </button>
    </div>
  );
};

/**
 * Metadata Section Component
 */
const MetadataSection: React.FC<{ dataset: Dataset }> = ({ dataset }) => {
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Dataset Information</h2>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Name</p>
          <p className="font-medium">{dataset.name}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Source</p>
          <p className="font-medium">{dataset.source_name || 'N/A'}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Status</p>
          <Badge variant={dataset.status === 'active' ? 'default' : 'secondary'}>
            {dataset.status}
          </Badge>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Row Count</p>
          <p className="font-medium">
            {dataset.row_count?.toLocaleString() || 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Size</p>
          <p className="font-medium">
            {dataset.size_bytes ? formatBytes(dataset.size_bytes) : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Created</p>
          <p className="font-medium">
            {format(new Date(dataset.created_at), 'MMM d, yyyy HH:mm')}
          </p>
        </div>
        {dataset.description && (
          <div className="col-span-2">
            <p className="text-sm text-muted-foreground">Description</p>
            <p className="font-medium">{dataset.description}</p>
          </div>
        )}
        {dataset.tags && dataset.tags.length > 0 && (
          <div className="col-span-2">
            <p className="text-sm text-muted-foreground mb-2">Tags</p>
            <div className="flex gap-2 flex-wrap">
              {dataset.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

/**
 * Linked Schema Section Component
 *
 * Shows either:
 * - A card with linked schema info (name, version, compatibility, linked date)
 *   plus an Unlink button with confirmation dialog.
 * - An "unlinked" banner with a Link Schema button that opens the SchemaPickerDialog.
 */
export const LinkedSchemaSection: React.FC<{
  datasetId: string;
  linkedSchema?: LinkedSchemaInfo | null;
}> = ({ datasetId, linkedSchema }) => {
  const { toast } = useToast();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);

  const linkMutation = useLinkDatasetToSchema();
  const unlinkMutation = useUnlinkDatasetFromSchema();

  const handleLink = async (schema: SchemaListItem) => {
    try {
      await linkMutation.mutateAsync({
        datasetId,
        schemaLibraryId: schema.id,
      });
      setPickerOpen(false);
      toast({
        title: 'Schema linked',
        description: `"${schema.name}" has been linked to this dataset.`,
      });
    } catch (error) {
      toast({
        title: 'Failed to link schema',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleUnlink = async () => {
    try {
      await unlinkMutation.mutateAsync(datasetId);
      setUnlinkConfirmOpen(false);
      toast({
        title: 'Schema unlinked',
        description: 'The schema library link has been removed from this dataset.',
      });
    } catch (error) {
      toast({
        title: 'Failed to unlink schema',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  if (!linkedSchema) {
    return (
      <>
        <Card className="p-6 border-dashed">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Unlink className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="font-medium">No linked schema</p>
                <p className="text-sm text-muted-foreground">
                  This dataset is not linked to a Schema Library entry.
                </p>
              </div>
            </div>
            <Button onClick={() => setPickerOpen(true)} disabled={linkMutation.isPending}>
              {linkMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Link2 className="h-4 w-4 mr-2" />
              Link Schema
            </Button>
          </div>
        </Card>

        <SchemaPickerDialog
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={handleLink}
        />
      </>
    );
  }

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Linked Schema</h3>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUnlinkConfirmOpen(true)}
            disabled={unlinkMutation.isPending}
          >
            {unlinkMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Unlink className="h-4 w-4 mr-2" />
            Unlink Schema
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Schema Name</p>
            <Link
              to={`/schemas`}
              className="font-medium text-primary hover:underline"
            >
              {linkedSchema.schema_name}
            </Link>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Version</p>
            <p className="font-medium">{linkedSchema.version}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Compatibility Mode</p>
            <CompatibilityBadge mode={linkedSchema.compatibility_mode as CompatibilityMode} />
          </div>
          {linkedSchema.linked_at && (
            <div>
              <p className="text-sm text-muted-foreground">Linked</p>
              <p className="font-medium">
                {format(new Date(linkedSchema.linked_at), 'MMM d, yyyy HH:mm')}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Unlink confirmation dialog */}
      <AlertDialog open={unlinkConfirmOpen} onOpenChange={setUnlinkConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Schema</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unlink &ldquo;{linkedSchema.schema_name}&rdquo; from this
              dataset? The dataset's data will not be modified -- only the library link will be
              removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnlink}>
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

/**
 * Schema Section Component
 */
const SchemaSection: React.FC<{ fields?: DatasetField[] }> = ({ fields }) => {
  if (!fields || fields.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground text-center">No schema information available</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Schema</h2>
      <UITable>
        <TableHeader>
          <TableRow>
            <TableHead>Column Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Nullable</TableHead>
            <TableHead>Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field, index) => (
            <TableRow key={index}>
              <TableCell className="font-medium">{field.name}</TableCell>
              <TableCell>
                <Badge variant="secondary">{field.type}</Badge>
              </TableCell>
              <TableCell>{field.nullable ? 'Yes' : 'No'}</TableCell>
              <TableCell className="text-muted-foreground">
                {field.description || '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </UITable>
    </Card>
  );
};

/**
 * Data Preview Section Component
 */
const PreviewSection: React.FC<{ preview?: DatasetPreview }> = ({ preview }) => {
  if (!preview || !preview.rows || preview.rows.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground text-center">No preview data available</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Data Preview</h2>
        <p className="text-sm text-muted-foreground">
          Showing {preview.preview_rows} of {preview.total_rows?.toLocaleString() ?? 'unknown'} rows
        </p>
      </div>
      <div className="overflow-x-auto">
        <UITable>
          <TableHeader>
            <TableRow>
              {preview.columns.map((column) => (
                <TableHead key={column}>{column}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {preview.rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {preview.columns.map((column) => (
                  <TableCell key={column}>
                    {row[column] !== null && row[column] !== undefined
                      ? String(row[column])
                      : '-'}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </UITable>
      </div>
    </Card>
  );
};

/**
 * Dataset Details Page Component
 */
export const DatasetDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Every TabsTrigger value must appear here: an omitted tab silently falls
  // back to 'metadata', so its trigger renders but can never be opened (#1481).
  const VALID_TABS = ['metadata', 'schema', 'preview', 'explore', 'classification'];
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam && VALID_TABS.includes(tabParam) ? tabParam : 'metadata';
  const handleTabChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', value);
        return next;
      },
      { replace: true },
    );
  };

  // Fetch dataset data — id is guaranteed defined after the guard below,
  // but hooks must be called unconditionally so we pass id ?? '' here.
  const { data: dataset, isLoading, error, refetch } = useDataset(id ?? '');

  // #964: schema and preview are fetched only once their tab has been opened.
  // Both were previously fetched on mount regardless of the active tab; because
  // the API serializes catalog reads onto one worker thread, those two unused
  // requests queued ahead of the one the Metadata tab actually renders.
  //
  // Enablement is sticky — once a tab has been visited its query stays enabled,
  // so tabbing back never re-suspends an already-loaded panel. Seeding from
  // `activeTab` keeps the deep-linkable `?tab=` form (#640) fetching on first
  // render.
  const [schemaRequested, setSchemaRequested] = useState<boolean>(activeTab === 'schema');
  const [previewRequested, setPreviewRequested] = useState<boolean>(activeTab === 'preview');

  // Sticky "has this tab ever been requested" flags are history-dependent (once
  // true they must stay true even after `activeTab` moves away), so they can't
  // be computed fresh each render — adjusted during render instead of an effect,
  // mirroring `activeTab` (a stable string primitive). Converges after exactly
  // one extra render, matching the DynamicConnectorForm sticky-announcement
  // precedent from the connectors sweep.
  const [prevActiveTabForRequest, setPrevActiveTabForRequest] = useState(activeTab);
  if (activeTab !== prevActiveTabForRequest) {
    setPrevActiveTabForRequest(activeTab);
    if (activeTab === 'schema') setSchemaRequested(true);
    if (activeTab === 'preview') setPreviewRequested(true);
  }

  const { data: schemaData } = useDatasetSchema(id ?? '', { enabled: schemaRequested });
  const { data: previewData } = useDatasetPreview(id ?? '', 100, { enabled: previewRequested });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);

  // Null guard: if the route param is missing, show an error instead of crashing
  if (!id) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Dataset ID is required</AlertDescription>
      </Alert>
    );
  }

  const handleBack = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">Loading dataset details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Datasets
        </Button>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load dataset: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!dataset) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Datasets
        </Button>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Dataset not found</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Button variant="ghost" onClick={handleBack} className="mb-2 -ml-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Datasets
          </Button>
          <InlineEditableName datasetId={id} name={dataset.name} />
          <InlineEditableDescription datasetId={id} description={dataset.description} />
        </div>
        <div className="flex gap-2">
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList>
          <TabsTrigger value="metadata">
            <Database className="h-4 w-4 mr-2" />
            Metadata
          </TabsTrigger>
          <TabsTrigger value="schema">
            <Table className="h-4 w-4 mr-2" />
            Schema
          </TabsTrigger>
          <TabsTrigger value="preview">
            <FileText className="h-4 w-4 mr-2" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="explore">
            <Search className="h-4 w-4 mr-2" />
            Explore
          </TabsTrigger>
          <TabsTrigger value="classification">
            <ShieldCheck className="h-4 w-4 mr-2" />
            Classification
          </TabsTrigger>
        </TabsList>

        <TabsContent value="metadata">
          <MetadataSection dataset={dataset} />
        </TabsContent>

        <TabsContent value="schema">
          <div className="space-y-6">
            <LinkedSchemaSection
              datasetId={id}
              linkedSchema={schemaData?.linked_schema}
            />
            <SchemaSection fields={schemaData?.fields} />
          </div>
        </TabsContent>

        <TabsContent value="preview">
          <PreviewSection preview={previewData} />
        </TabsContent>

        <TabsContent value="explore">
          <DatasetExplore datasetId={id} />
        </TabsContent>

        <TabsContent value="classification">
          <ClassificationSection datasetId={id} />
        </TabsContent>
      </Tabs>

      {/* Delete (soft) / permanent-delete dialog */}
      <DeleteDatasetDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        dataset={{ id, name: dataset.name }}
        onDeleted={() => navigate('/datasets')}
      />
    </div>
  );
};

export { InlineEditableName, InlineEditableDescription };
